/**
 * BO page para el user simulator (Fase 39).
 *
 * 3 tabs en un solo componente para mantener el footprint chico (MVP):
 *   - Catalog:   read-only de personas + scenarios cargados del YAML.
 *   - New batch: form que arma un BatchSpec + dry-run estimate + trigger.
 *   - Batches:   lista histórica con link a detalle inline.
 *
 * Estilos: usa las clases canónicas del design system del BO. Patrón de
 * layout es `.config-editor > .config-section` para que cada bloque tenga
 * padding propio. Inputs/selects usan `.input` (standalone añadido en
 * tokens.css). Tablas usan `.table` con `.r` para alineación derecha.
 */
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelSimulatorBatch,
  fetchSimulatorBatch,
  fetchSimulatorBatches,
  fetchSimulatorCatalog,
  estimateSimulatorBatchCost,
  resetSimulatorPersona,
  triggerSimulatorBatch,
  type SimulatorBatchRequest,
  type SimulatorCatalog,
  type SimulatorPersona,
} from '../../api/admin/simulator.js';
import { AdminApiError } from '../../api/admin/_core.js';

type Tab = 'catalog' | 'new' | 'batches';

export function SimulatorPage(): ReactElement {
  const [tab, setTab] = useState<Tab>('new');
  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">User simulator</h1>
          <p className="page-sub">
            Genera sesiones sintéticas para validar el agente sin tener que
            hablar con el orb en vivo. <strong>No descuenta créditos</strong>;
            no contamina métricas reales (filtradas por <code>kind</code>).
          </p>
        </div>
      </div>
      <div className="config-page">
        <section className="card">
          <header className="card-head config-tabs">
            <div className="segmented" role="tablist" aria-label="Vista">
              <TabButton id="catalog" tab={tab} setTab={setTab}>
                Catálogo
              </TabButton>
              <TabButton id="new" tab={tab} setTab={setTab}>
                Nuevo batch
              </TabButton>
              <TabButton id="batches" tab={tab} setTab={setTab}>
                Batches
              </TabButton>
            </div>
          </header>
          <div className="config-editor">
            {tab === 'catalog' && <CatalogView />}
            {tab === 'new' && <NewBatchView />}
            {tab === 'batches' && <BatchesView />}
          </div>
        </section>
      </div>
    </>
  );
}

function TabButton(props: {
  id: Tab;
  tab: Tab;
  setTab: (t: Tab) => void;
  children: React.ReactNode;
}): ReactElement {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={props.tab === props.id}
      className={props.tab === props.id ? 'is-active' : ''}
      onClick={() => props.setTab(props.id)}
    >
      {props.children}
    </button>
  );
}

function SectionHead({ title, sub }: { title: string; sub?: string }): ReactElement {
  return (
    <div className="config-section-head">
      <h3 className="config-section-title">{title}</h3>
      {sub && <p className="config-section-sub">{sub}</p>}
    </div>
  );
}

function CatalogView(): ReactElement {
  const query = useQuery({
    queryKey: ['admin', 'simulator', 'catalog'],
    queryFn: fetchSimulatorCatalog,
  });
  if (query.isPending) return <div className="config-section">Cargando…</div>;
  if (query.isError)
    return (
      <div className="config-section">
        <ErrorBanner err={query.error} />
      </div>
    );
  const cat = query.data;
  return (
    <>
      <section className="config-section">
        <SectionHead
          title={`Personas (${cat.personas.length})`}
          sub="Cargadas del package @orborix/sim-personas. Read-only — editar el YAML requiere PR + restart."
        />
        <table className="table">
          <thead>
            <tr>
              <th>Slug</th>
              <th>Nombre</th>
              <th>Módulo</th>
              <th>CEFR</th>
              <th>Native</th>
              <th>Free-form</th>
              <th className="r">Reset</th>
            </tr>
          </thead>
          <tbody>
            {cat.personas.map((p) => (
              <PersonaRow key={p.slug} persona={p} />
            ))}
          </tbody>
        </table>
      </section>
      <section className="config-section">
        <SectionHead
          title={`Scenarios (${cat.scenarios.length})`}
          sub="Cargados del package @orborix/sim-scenarios. Modos: task (con goal + criterios) y free_form (chitchat)."
        />
        <table className="table">
          <thead>
            <tr>
              <th>Slug</th>
              <th>Título</th>
              <th>Modo</th>
              <th>Módulos</th>
              <th>Niveles</th>
              <th className="r">Max turns</th>
            </tr>
          </thead>
          <tbody>
            {cat.scenarios.map((s) => (
              <tr key={s.slug}>
                <td>
                  <code className="mono">{s.slug}</code>
                </td>
                <td>{s.title}</td>
                <td>{s.mode}</td>
                <td className="muted">{s.applicable_modules.join(', ')}</td>
                <td className="muted">{s.applicable_levels.join(', ')}</td>
                <td className="r">{s.max_turns}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}

function PersonaRow({ persona }: { persona: SimulatorPersona }): ReactElement {
  const [busy, setBusy] = useState(false);
  const mutation = useMutation({
    mutationFn: () => resetSimulatorPersona(persona.slug),
  });
  return (
    <tr>
      <td>
        <code className="mono">{persona.slug}</code>
      </td>
      <td>{persona.name}</td>
      <td>{persona.target_module}</td>
      <td>{persona.cefr}</td>
      <td className="muted">
        {persona.native_language.toUpperCase()} / {persona.country.toUpperCase()}
      </td>
      <td>{persona.has_free_form ? '✓' : '·'}</td>
      <td className="r">
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={async () => {
            if (!confirm(`Wipe memoria y sesiones de ${persona.slug}?`)) return;
            setBusy(true);
            try {
              const r = await mutation.mutateAsync();
              alert(
                `Reseteado: ${r.sessionsDeleted} sesiones + ${r.factsDeleted} facts borrados`,
              );
            } catch (err) {
              alert(`Error: ${err instanceof Error ? err.message : String(err)}`);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? '…' : 'Reset'}
        </button>
      </td>
    </tr>
  );
}

interface ComboDraft {
  personaSlug: string;
  scenarioSlug: string;
  count: number;
}

function NewBatchView(): ReactElement {
  const qc = useQueryClient();
  const catalog = useQuery({
    queryKey: ['admin', 'simulator', 'catalog'],
    queryFn: fetchSimulatorCatalog,
  });
  const [combos, setCombos] = useState<ComboDraft[]>([]);
  const [concurrency, setConcurrency] = useState(5);
  const [seed, setSeed] = useState<string>('');
  // Modelo del simulator persistido en localStorage para que recuerde la
  // última elección entre recargas/sesiones del BO. Si no hay nada guardado
  // cae al default del backend cuando catalog carga.
  const [model, setModel] = useState<string>(
    () => localStorage.getItem('simulator.lastModel') ?? '',
  );
  // Fase 39.x — flags opt-out persistidos. Defaults: skipTts/Analyze=true,
  // skipDistill=false (mismos que el backend). Cargados de localStorage si
  // existen; sino los del default.
  const [skipTts, setSkipTts] = useState<boolean>(
    () => readBoolFlag('simulator.skipTts', true),
  );
  const [skipAnalyze, setSkipAnalyze] = useState<boolean>(
    () => readBoolFlag('simulator.skipAnalyze', true),
  );
  const [skipDistill, setSkipDistill] = useState<boolean>(
    () => readBoolFlag('simulator.skipDistill', false),
  );
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const spec: SimulatorBatchRequest = useMemo(
    () => ({
      combinations: combos.map((c) => ({ ...c })),
      concurrency,
      ...(seed.trim() ? { seed: Number(seed.trim()) } : {}),
      simulatorModel: model,
      skipTts,
      skipAnalyze,
      skipDistill,
    }),
    [combos, concurrency, seed, model, skipTts, skipAnalyze, skipDistill],
  );

  const estimate = useQuery({
    queryKey: ['admin', 'simulator', 'estimate', JSON.stringify(spec)],
    queryFn: () => estimateSimulatorBatchCost(spec),
    enabled: combos.length > 0,
  });

  const trigger = useMutation({
    mutationFn: () => triggerSimulatorBatch(spec),
    onSuccess: () => {
      setToast({ kind: 'ok', text: 'Batch disparado — ver tab Batches' });
      void qc.invalidateQueries({ queryKey: ['admin', 'simulator', 'batches'] });
      setCombos([]);
    },
    onError: (err: unknown) =>
      setToast({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Error',
      }),
  });

  // Cuando el catálogo carga, si el modelo guardado no existe (o no había
  // nada guardado), seteamos al default del backend. Evita el caso donde
  // un modelo persistido en localStorage fue eliminado del catálogo.
  useEffect(() => {
    if (!catalog.data) return;
    const valid = catalog.data.simulatorModels.some((m) => m.id === model);
    if (!model || !valid) {
      setModel(catalog.data.defaultModel ?? '');
    }
  }, [catalog.data, model]);

  function updateModel(next: string): void {
    setModel(next);
    try {
      localStorage.setItem('simulator.lastModel', next);
    } catch {
      /* storage may be unavailable (private mode) — fine */
    }
  }

  if (catalog.isPending) return <div className="config-section">Cargando catálogo…</div>;
  if (catalog.isError)
    return (
      <div className="config-section">
        <ErrorBanner err={catalog.error} />
      </div>
    );
  const cat = catalog.data;
  const totalSessions = combos.reduce((s, c) => s + c.count, 0);

  return (
    <>
      <ComboBuilder catalog={cat} combos={combos} setCombos={setCombos} />
      <section className="config-section">
        <SectionHead title="Run config" sub="Cómo se ejecuta el batch." />
        <div className="sim-form-row">
          <label className="sim-field">
            <span className="config-field-label">Concurrency</span>
            <input
              className="input"
              type="number"
              min={1}
              max={20}
              value={concurrency}
              onChange={(e) => setConcurrency(Number(e.target.value))}
            />
          </label>
          <label className="sim-field">
            <span className="config-field-label">Seed (opcional)</span>
            <input
              className="input"
              type="text"
              placeholder="42"
              value={seed}
              onChange={(e) => setSeed(e.target.value)}
            />
          </label>
          <label className="sim-field sim-field-wide">
            <span className="config-field-label">Simulator model</span>
            <select
              className="input"
              value={model}
              onChange={(e) => updateModel(e.target.value)}
            >
              {model === '' ? (
                <option value="" disabled>
                  Elegí un modelo
                </option>
              ) : null}
              {cat.simulatorModels.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName} ({m.tier}
                  {m.status === 'experimental' ? ' · experimental' : ''})
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="config-field-hint" style={{ marginTop: 'var(--s-5)' }}>
          Optimizaciones de costo (recomendado mantener los defaults para validar prompts):
        </p>
        <div className="sim-toggle-row">
          <ToggleRow
            label="Skip TTS"
            checked={skipTts}
            onChange={(v) => {
              setSkipTts(v);
              writeBoolFlag('simulator.skipTts', v);
            }}
            hint="Saltea la generación de audio. ~47% menos costo y 3-4× más rápido por turno."
          />
          <ToggleRow
            label="Skip session.analyze"
            checked={skipAnalyze}
            onChange={(v) => {
              setSkipAnalyze(v);
              writeBoolFlag('simulator.skipAnalyze', v);
            }}
            hint="No encola el job offline de análisis (Fase 38). ~$0.5 USD menos por sesión."
          />
          <ToggleRow
            label="Skip session.distill"
            checked={skipDistill}
            onChange={(v) => {
              setSkipDistill(v);
              writeBoolFlag('simulator.skipDistill', v);
            }}
            hint="Si lo marcás, NO se acumula memoria en el service account entre batches."
          />
        </div>
      </section>
      {combos.length > 0 && estimate.data && (
        <section className="config-section">
          <SectionHead
            title="Cost estimate"
            sub="Usa promedios reales de runs históricos cuando hay ≥3 corridas; sino cae a heurístico calibrado."
          />
          <p className="config-field-hint">
            <strong>{estimate.data.totalSessions}</strong> sesiones ·{' '}
            <strong>{estimate.data.approxTurns}</strong> turnos aproximados ·{' '}
            simulator ≈ <strong>USD {estimate.data.simulatorCostUsd.toFixed(4)}</strong>
            {estimate.data.agentCostUsd != null && (
              <>
                {' · '}agente ≈ <strong>USD {estimate.data.agentCostUsd.toFixed(4)}</strong>
                {' · '}total ≈ <strong>USD {estimate.data.totalCostUsd?.toFixed(4)}</strong>
              </>
            )}
            <br />
            <span className="muted">{estimate.data.note}</span>
          </p>
          {estimate.data.breakdown.length > 0 && (
            <table className="table sim-table-spaced">
              <thead>
                <tr>
                  <th>Persona</th>
                  <th>Scenario</th>
                  <th className="r">Count</th>
                  <th>Source</th>
                  <th className="r">Avg turns</th>
                  <th className="r">In/turn</th>
                  <th className="r">Out/turn</th>
                </tr>
              </thead>
              <tbody>
                {estimate.data.breakdown.map((b, i) => (
                  <tr key={i}>
                    <td>
                      <code className="mono">{b.personaSlug}</code>
                    </td>
                    <td>
                      <code className="mono">{b.scenarioSlug}</code>
                    </td>
                    <td className="r">{b.count}</td>
                    <td>
                      {b.source === 'historical' ? (
                        <span className="badge">hist · {b.historicalRuns} runs</span>
                      ) : (
                        <span className="badge" title="Sin data histórica; usando heurístico">
                          fallback
                        </span>
                      )}
                    </td>
                    <td className="r">{b.avgTurns}</td>
                    <td className="r mono">{b.avgInPerTurn}</td>
                    <td className="r mono">{b.avgOutPerTurn}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
      <footer className="config-footer">
        <div className="config-footer-left">
          {toast && (
            <span
              className={toast.kind === 'ok' ? 'config-toast ok' : 'config-toast err'}
              role="status"
            >
              {toast.text}
            </span>
          )}
        </div>
        <div className="config-footer-actions">
          <button
            type="button"
            className="btn-primary"
            disabled={combos.length === 0 || trigger.isPending}
            onClick={() => trigger.mutate()}
          >
            {trigger.isPending ? 'Disparando…' : `Disparar batch (${totalSessions} sesiones)`}
          </button>
        </div>
      </footer>
    </>
  );
}

function ComboBuilder(props: {
  catalog: SimulatorCatalog;
  combos: ComboDraft[];
  setCombos: (c: ComboDraft[]) => void;
}): ReactElement {
  const [personaSlug, setPersonaSlug] = useState<string>(
    props.catalog.personas[0]?.slug ?? '',
  );
  const [scenarioSlug, setScenarioSlug] = useState<string>(
    props.catalog.scenarios[0]?.slug ?? '',
  );
  const [count, setCount] = useState(3);

  const filteredScenarios = useMemo(() => {
    const persona = props.catalog.personas.find((p) => p.slug === personaSlug);
    if (!persona) return props.catalog.scenarios;
    return props.catalog.scenarios.filter(
      (s) =>
        s.applicable_modules.includes(persona.target_module) &&
        s.applicable_levels.includes(persona.cefr),
    );
  }, [props.catalog, personaSlug]);

  useEffect(() => {
    if (!filteredScenarios.find((s) => s.slug === scenarioSlug)) {
      setScenarioSlug(filteredScenarios[0]?.slug ?? '');
    }
  }, [filteredScenarios, scenarioSlug]);

  function addCombo(): void {
    if (!personaSlug || !scenarioSlug) return;
    props.setCombos([...props.combos, { personaSlug, scenarioSlug, count }]);
  }

  return (
    <section className="config-section">
      <SectionHead
        title="Combinaciones"
        sub="Elegí persona + scenario + cuántas sesiones. Las opciones de scenario se filtran por módulo + CEFR de la persona."
      />
      <div className="sim-form-row">
        <label className="sim-field sim-field-wide">
          <span className="config-field-label">Persona</span>
          <select
            className="input"
            value={personaSlug}
            onChange={(e) => setPersonaSlug(e.target.value)}
          >
            {props.catalog.personas.map((p) => (
              <option key={p.slug} value={p.slug}>
                {p.name} · {p.cefr} ({p.slug})
              </option>
            ))}
          </select>
        </label>
        <label className="sim-field sim-field-wide">
          <span className="config-field-label">Scenario</span>
          <select
            className="input"
            value={scenarioSlug}
            onChange={(e) => setScenarioSlug(e.target.value)}
          >
            {filteredScenarios.map((s) => (
              <option key={s.slug} value={s.slug}>
                {s.title} [{s.mode}]
              </option>
            ))}
          </select>
        </label>
        <label className="sim-field">
          <span className="config-field-label">Count</span>
          <input
            className="input"
            type="number"
            min={1}
            max={50}
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
          />
        </label>
        <div className="sim-field-actions">
          <button type="button" className="btn" onClick={addCombo}>
            + Agregar
          </button>
        </div>
      </div>
      {props.combos.length > 0 && (
        <table className="table sim-table-spaced">
          <thead>
            <tr>
              <th>Persona</th>
              <th>Scenario</th>
              <th className="r">Count</th>
              <th className="r"></th>
            </tr>
          </thead>
          <tbody>
            {props.combos.map((c, i) => (
              <tr key={i}>
                <td>
                  <code className="mono">{c.personaSlug}</code>
                </td>
                <td>
                  <code className="mono">{c.scenarioSlug}</code>
                </td>
                <td className="r">{c.count}</td>
                <td className="r">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      const next = [...props.combos];
                      next.splice(i, 1);
                      props.setCombos(next);
                    }}
                  >
                    Quitar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function BatchesView(): ReactElement {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const cancelMutation = useMutation({
    mutationFn: (id: string) => cancelSimulatorBatch(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'simulator', 'batches'] });
    },
  });
  const list = useQuery({
    queryKey: ['admin', 'simulator', 'batches'],
    queryFn: fetchSimulatorBatches,
    refetchInterval: 5000,
  });
  if (list.isPending) return <div className="config-section">Cargando…</div>;
  if (list.isError)
    return (
      <div className="config-section">
        <ErrorBanner err={list.error} />
      </div>
    );
  const batches = list.data ?? [];

  return (
    <>
      <section className="config-section">
        <SectionHead
          title="Batches"
          sub="Histórico de disparos del simulator. Refresh automático cada 5s."
        />
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Triggered</th>
              <th>Status</th>
              <th className="r">Sesiones</th>
              <th className="r">Completadas</th>
              <th className="r">Fallaron</th>
              <th className="r"></th>
            </tr>
          </thead>
          <tbody>
            {batches.map((b) => (
              <tr key={b.id} className={selected === b.id ? 'row is-active' : 'row'}>
                <td>
                  <code className="mono">{b.id.slice(0, 8)}…</code>
                </td>
                <td className="muted">{new Date(b.triggered_at).toLocaleString()}</td>
                <td>
                  <StatusBadge status={b.status} />
                </td>
                <td className="r">{b.sessions_total}</td>
                <td className="r">{b.sessions_completed}</td>
                <td className="r">{b.sessions_failed}</td>
                <td className="r">
                  <div className="sim-row-actions">
                    {b.status === 'running' && (
                      <button
                        type="button"
                        className="btn"
                        title="Cancelar batch (no aborta sesiones en vuelo)"
                        disabled={cancelMutation.isPending}
                        onClick={() => {
                          if (confirm(`Cancelar batch ${b.id.slice(0, 8)}…?`)) {
                            cancelMutation.mutate(b.id);
                          }
                        }}
                      >
                        Cancelar
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setSelected(selected === b.id ? null : b.id)}
                    >
                      {selected === b.id ? 'Cerrar' : 'Ver'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {batches.length === 0 && (
              <tr>
                <td colSpan={7} className="muted">
                  Aún no hay batches.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
      {selected && <BatchDetail id={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function BatchDetail({ id, onClose }: { id: string; onClose: () => void }): ReactElement {
  const detail = useQuery({
    queryKey: ['admin', 'simulator', 'batch', id],
    queryFn: () => fetchSimulatorBatch(id),
    refetchInterval: 3000,
  });
  return (
    <section className="config-section">
      <div className="sim-detail-head">
        <SectionHead title={`Batch ${id}`} sub="Runs individuales del batch. Refresh 3s." />
        <button type="button" className="btn" onClick={onClose}>
          Cerrar
        </button>
      </div>
      {detail.isPending && <div className="muted">Cargando…</div>}
      {detail.isError && <ErrorBanner err={detail.error} />}
      {detail.data && (
        <table className="table">
          <thead>
            <tr>
              <th>Persona</th>
              <th>Scenario</th>
              <th>Módulo</th>
              <th>Status</th>
              <th>End reason</th>
              <th className="r">Turns</th>
              <th className="r">Tokens (in/out)</th>
            </tr>
          </thead>
          <tbody>
            {detail.data.runs.map((r) => (
              <tr key={r.id}>
                <td>
                  <code className="mono">{r.persona_slug}</code>
                </td>
                <td>
                  <code className="mono">{r.scenario_slug}</code>
                </td>
                <td>{r.module_slug}</td>
                <td>
                  <StatusBadge status={r.status} />
                </td>
                <td className="muted">{r.end_reason ?? '-'}</td>
                <td className="r">{r.turn_count}</td>
                <td className="r mono">
                  {r.simulator_total_input_tokens} / {r.simulator_total_output_tokens}
                </td>
              </tr>
            ))}
            {detail.data.runs.length === 0 && (
              <tr>
                <td colSpan={7} className="muted">
                  Sin runs todavía…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </section>
  );
}

function StatusBadge({ status }: { status: string }): ReactElement {
  return <span className={`chip is-on chip-status chip-status-${status}`}>{status}</span>;
}

function ErrorBanner({ err }: { err: unknown }): ReactElement {
  if (err instanceof AdminApiError) {
    return (
      <div className="config-state config-state-err">
        Error {err.code}: {err.message}
      </div>
    );
  }
  return (
    <div className="config-state config-state-err">
      {err instanceof Error ? err.message : String(err)}
    </div>
  );
}

function ToggleRow(props: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint: string;
}): ReactElement {
  return (
    <label className="sim-toggle">
      <input
        type="checkbox"
        checked={props.checked}
        onChange={(e) => props.onChange(e.target.checked)}
      />
      <span className="sim-toggle-body">
        <span className="sim-toggle-label">{props.label}</span>
        <span className="sim-toggle-hint">{props.hint}</span>
      </span>
    </label>
  );
}

function readBoolFlag(key: string, fallback: boolean): boolean {
  try {
    const raw = localStorage.getItem(key);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch {
    /* private mode / disabled storage */
  }
  return fallback;
}

function writeBoolFlag(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    /* private mode / disabled storage */
  }
}
